import React, { useState, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import {
  Box, Heading, FormControl, FormLabel, Select, Input, HStack, Button,
  FormErrorMessage, Tooltip, Icon, Text
} from '@chakra-ui/react';
import { InfoIcon } from '@chakra-ui/icons';
import { formDataState } from '../../state/calculatorState';
import { finalHoleDiameterOptions } from '../../utils/complexityCalculator';

const SURFACE_TEMP_C = 15;
const SURFACE_TEMP_F = 59;

const TOOLTIPS = {
  reEntry: 'Well is a re-entry of an existing wellbore',
  function: 'Primary purpose of the well (Production, Injection, Dual, Storage, or Data acquisition)',
  finalHoleDiameter: 'Final open hole diameter at total depth',
  numberOfSections: 'Total number of casing/liner sections in the well design',
  verticalDepth: 'True vertical depth to the deepest point of the well',
  maxTemperature: 'Maximum expected bottomhole temperature',
  maxPressure: 'Maximum expected formation pressure',
  stimulation: 'Well requires hydraulic fracturing or other stimulation',
  scaling: 'Risk of mineral scale formation in the wellbore',
  corrosion: 'Risk of corrosive fluids or gases'
};

// #4: Limit depth to 12000m (deepest hole ever was ~12000m Kola Superdeep)
const INPUT_BOUNDS_METRIC = {
  verticalDepth: { min: 0, max: 12000, label: 'Vertical Depth' },
  maxTemperature: { min: 0, max: 600, label: 'Max Temperature' },
  maxPressure: { min: 0, max: 2000, label: 'Max Pressure' }
};

const INPUT_BOUNDS_IMPERIAL = {
  verticalDepth: { min: 0, max: 39370, label: 'Vertical Depth' }, // ~12000m in ft
  maxTemperature: { min: 32, max: 1112, label: 'Max Temperature' }, // °F
  maxPressure: { min: 0, max: 29000, label: 'Max Pressure' } // psi
};

const SUPERCRITICAL_TEMP_C = 374;
const SUPERCRITICAL_TEMP_F = 705; // ~374°C in Fahrenheit

const LabelWithInfo = ({ children, tooltip }) => (
  <HStack spacing={1}>
    <span>{children}</span>
    <Tooltip label={tooltip} placement="top" hasArrow>
      <span><Icon as={InfoIcon} boxSize={3} color="gray.400" cursor="help" /></span>
    </Tooltip>
  </HStack>
);

export const DesignSection = ({ compact }) => {
  const [formData, setFormData] = useRecoilState(formDataState);
  const [errors, setErrors] = useState({});

  // Determine bounds based on units
  const isMetric = formData.units === 'metric';
  const INPUT_BOUNDS = isMetric ? INPUT_BOUNDS_METRIC : INPUT_BOUNDS_IMPERIAL;
  const supercriticalThreshold = isMetric ? SUPERCRITICAL_TEMP_C : SUPERCRITICAL_TEMP_F;

  // Unit labels
  const depthUnit = isMetric ? 'm' : 'ft';
  const tempUnit = isMetric ? 'C' : 'F';
  const pressureUnit = isMetric ? 'bar' : 'psi';

  // Calculate geothermal gradient
  const geothermalGradient = useMemo(() => {
    const depth = formData.verticalDepth;
    const maxTemp = formData.maxTemperature;

    if (!depth || depth <= 0) return null;

    if (isMetric) {
      // °C/km = (maxTemp - surfaceTemp) / (depth in m) * 1000
      const gradient = ((maxTemp - SURFACE_TEMP_C) / depth) * 1000;
      return gradient > 0 ? gradient.toFixed(1) : null;
    } else {
      // °F/1000ft = (maxTemp - surfaceTemp) / (depth in ft) * 1000
      const gradient = ((maxTemp - SURFACE_TEMP_F) / depth) * 1000;
      return gradient > 0 ? gradient.toFixed(1) : null;
    }
  }, [formData.verticalDepth, formData.maxTemperature, isMetric]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    const num = parseFloat(value);
    const bounds = INPUT_BOUNDS[field];
    setErrors(prev => ({ ...prev, [field]: null }));

    if (isNaN(num)) {
      setFormData(prev => ({ ...prev, [field]: 0 }));
      return;
    }

    if (bounds) {
      if (num < bounds.min) {
        setErrors(prev => ({ ...prev, [field]: `${bounds.label} cannot be less than ${bounds.min}` }));
      } else if (num > bounds.max) {
        setErrors(prev => ({ ...prev, [field]: `${bounds.label} cannot exceed ${bounds.max}` }));
      }
    }

    const clampedValue = bounds ? Math.max(bounds.min, Math.min(bounds.max, num)) : num;

    // Auto-set phase to supercritical when temperature exceeds threshold
    if (field === 'maxTemperature' && clampedValue > supercriticalThreshold) {
      setFormData(prev => ({ ...prev, [field]: clampedValue, phase: 'high' }));
    } else {
      setFormData(prev => ({ ...prev, [field]: clampedValue }));
    }
  };

  const handleBoolToggle = (field) => {
    setFormData(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <Box bg="white" p={compact ? 0 : 5} borderRadius={compact ? 0 : 'lg'} boxShadow={compact ? 'none' : 'sm'}>
      {!compact && <Heading size="md" mb={4}>Design</Heading>}

      <Tooltip label={TOOLTIPS.reEntry} placement="top" hasArrow>
        <Button
          size="xs"
          mb={3}
          role="switch"
          aria-checked={formData.reEntry}
          variant={formData.reEntry ? 'solid' : 'outline'}
          colorScheme={formData.reEntry ? 'blue' : 'gray'}
          onClick={() => handleBoolToggle('reEntry')}
        >
          Re-Entry
        </Button>
      </Tooltip>

      <FormControl mb={2}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.function}>Function</LabelWithInfo>
        </FormLabel>
        <Select size="sm" value={formData.Function} onChange={(e) => handleChange('Function', e.target.value)}>
          <option value="" disabled>Select...</option>
          <option value="Production">Production</option>
          <option value="Injection">Injection</option>
          <option value="Dual">Dual</option>
          <option value="Storage">Storage</option>
          <option value="Data acquisition">Data acquisition</option>
        </Select>
      </FormControl>

      <FormControl mb={2}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.finalHoleDiameter}>Final Hole Diameter</LabelWithInfo>
        </FormLabel>
        <Select size="sm" value={formData.finalHoleDiameter} onChange={(e) => handleNumberChange('finalHoleDiameter', e.target.value)}>
          <option value="" disabled>Select...</option>
          {finalHoleDiameterOptions.map(({ value, label }) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </Select>
      </FormControl>

      <FormControl mb={2}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.numberOfSections}>No. of Sections</LabelWithInfo>
        </FormLabel>
        <Select size="sm" value={formData.numberOfSections} onChange={(e) => handleNumberChange('numberOfSections', e.target.value)}>
          <option value="" disabled>Select...</option>
          {[2, 3, 4, 5, 6, 7].map(n => (
            <option key={n} value={n}>{n}</option>
          ))}
        </Select>
      </FormControl>

      <FormControl mb={2} isInvalid={!!errors.verticalDepth}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.verticalDepth}>{`Vertical Depth (${depthUnit})`}</LabelWithInfo>
        </FormLabel>
        <Input size="sm" type="number" min={INPUT_BOUNDS.verticalDepth.min} max={INPUT_BOUNDS.verticalDepth.max} value={formData.verticalDepth || ''} placeholder={isMetric ? 'Example: 3000' : 'Example: 10000'} onChange={(e) => handleNumberChange('verticalDepth', e.target.value)} />
        {errors.verticalDepth && <FormErrorMessage>{errors.verticalDepth}</FormErrorMessage>}
      </FormControl>

      <FormControl mb={2} isInvalid={!!errors.maxTemperature}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.maxTemperature}>{`Max Temperature (${tempUnit})`}</LabelWithInfo>
        </FormLabel>
        <Input size="sm" type="number" min={INPUT_BOUNDS.maxTemperature.min} max={INPUT_BOUNDS.maxTemperature.max} value={formData.maxTemperature || ''} placeholder={isMetric ? 'Example: 200' : 'Example: 400'} onChange={(e) => handleNumberChange('maxTemperature', e.target.value)} />
        {errors.maxTemperature && <FormErrorMessage>{errors.maxTemperature}</FormErrorMessage>}
      </FormControl>

      <Box mb={2} px={2} py={1} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.600">
          Geothermal Gradient: {geothermalGradient ? `${geothermalGradient} ${isMetric ? '°C/km' : '°F/1000ft'}` : '--'}
        </Text>
      </Box>

      <FormControl mb={3} isInvalid={!!errors.maxPressure}>
        <FormLabel fontSize="sm" fontWeight="semibold">
          <LabelWithInfo tooltip={TOOLTIPS.maxPressure}>{`Max Pressure (${pressureUnit})`}</LabelWithInfo>
        </FormLabel>
        <Input size="sm" type="number" min={INPUT_BOUNDS.maxPressure.min} max={INPUT_BOUNDS.maxPressure.max} value={formData.maxPressure || ''} placeholder={isMetric ? 'Example: 500' : 'Example: 7250'} onChange={(e) => handleNumberChange('maxPressure', e.target.value)} />
        {errors.maxPressure && <FormErrorMessage>{errors.maxPressure}</FormErrorMessage>}
      </FormControl>

      <HStack spacing={2} flexWrap="wrap">
        <Tooltip label={TOOLTIPS.stimulation} placement="top" hasArrow>
          <Button size="xs" role="switch" aria-checked={formData.stimulation} variant={formData.stimulation ? 'solid' : 'outline'} colorScheme={formData.stimulation ? 'blue' : 'gray'} onClick={() => handleBoolToggle('stimulation')}>
            Stimulation
          </Button>
        </Tooltip>
        <Tooltip label={TOOLTIPS.scaling} placement="top" hasArrow>
          <Button size="xs" role="switch" aria-checked={formData.scaling} variant={formData.scaling ? 'solid' : 'outline'} colorScheme={formData.scaling ? 'blue' : 'gray'} onClick={() => handleBoolToggle('scaling')}>
            Scaling
          </Button>
        </Tooltip>
        <Tooltip label={TOOLTIPS.corrosion} placement="top" hasArrow>
          <Button size="xs" role="switch" aria-checked={formData.corrosion} variant={formData.corrosion ? 'solid' : 'outline'} colorScheme={formData.corrosion ? 'blue' : 'gray'} onClick={() => handleBoolToggle('corrosion')}>
            Corrosion
          </Button>
        </Tooltip>
      </HStack>
    </Box>
  );
};

export default DesignSection;
